import { useEffect, type ReactNode } from 'react';

import { ArchivedChatsSettings } from './archived-chats-settings';
import { CustomSkillsSettings } from './custom-skills-settings';
import { NotificationSettings } from './notification-settings';
import { NovaAnalyticsSettings } from './nova-analytics-settings/nova-analytics-settings';
import { NovaSchedulesSettings } from './nova-schedules-settings';
import { OutlookConnectSettings } from './outlook-connect-settings';
import { AdminsSettings, TeamsSettings } from './skill-administration';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { useActiveSettingsSection } from '../hooks/use-active-settings-section';
import { useNovaNavigationSafe } from '../navigation/nova-navigation-context';
import type { NovaSettingsSection } from '../navigation/types';

/** Shared section rendering; hosts can supply their own General content. */
export function ChatSettingsPanel({
  showOutlookConnect = true,
  generalContent,
  section,
}: {
  showOutlookConnect?: boolean;
  generalContent?: ReactNode;
  section?: NovaSettingsSection;
}) {
  const { settingsSection, setSettingsSection, setIsSettingsOpen, requestSettingsClose } = useChatOverlayContext();
  const activeSettingsSection = useActiveSettingsSection(section);
  const navigation = useNovaNavigationSafe();

  useEffect(() => {
    if ((settingsSection === 'teams' || settingsSection === 'admins') && activeSettingsSection === 'general') {
      if (navigation) {
        navigation.actions.openSettings('general');
        return;
      }
      requestSettingsClose(() => {
        setSettingsSection('general');
        setIsSettingsOpen(true);
      });
    }
  }, [activeSettingsSection, navigation, requestSettingsClose, setIsSettingsOpen, setSettingsSection, settingsSection]);

  const generalActive = activeSettingsSection === 'general';
  return (
    <div className="flex size-full min-h-0 min-w-0 flex-col">
      <div
        className={`flex min-h-0 flex-1 items-start justify-center overflow-y-auto [scrollbar-gutter:stable_both-edges] ${
          generalActive
            ? 'p-6'
            : activeSettingsSection === 'analytics'
              ? 'px-4 pt-10 pb-8 sm:px-8'
              : 'px-4 pt-6 pb-8 sm:px-8 sm:pt-10'
        }`}
      >
        <div className="flex w-[736px] max-w-full shrink-0 flex-col gap-4">
          {/* Host-specific General content stays mounted while switching sections. */}
          {generalContent !== undefined ? (
            <div
              hidden={!generalActive}
              className={generalActive ? 'flex w-full max-w-md flex-col gap-4 self-center' : 'hidden'}
            >
              {generalContent}
            </div>
          ) : generalActive ? (
            <div className="flex w-full max-w-md flex-col gap-4 self-center">
              <NotificationSettings />
              {showOutlookConnect ? <OutlookConnectSettings /> : null}
              <ArchivedChatsSettings />
            </div>
          ) : null}
          {activeSettingsSection === 'skills' ? <CustomSkillsSettings layout="page" /> : null}
          {activeSettingsSection === 'scheduled' ? <NovaSchedulesSettings /> : null}
          {activeSettingsSection === 'teams' ? <TeamsSettings /> : null}
          {activeSettingsSection === 'admins' ? <AdminsSettings /> : null}
          {activeSettingsSection === 'analytics' ? <NovaAnalyticsSettings /> : null}
        </div>
      </div>
    </div>
  );
}
